import { publishNativeGeolocationPosition } from './nativeGeolocationHostBridge';
import { isNativeWebViewPageUnloading } from './nativeWebViewLifecycle';

/**
 * **Host integration (recommended upstream):** Native apps should prefer `WKScriptMessageHandler`
 * / `postMessage` and let the page use `navigator.geolocation` (W3C Geolocation API). When a host
 * instead calls `evaluateJavaScript("getLocation(lat, lng)")`, the page must expose that global.
 *
 * **This module (web-side):** If `globalThis.window.getLocation` is missing, installs a forwarder that
 * validates coordinates and publishes a synthetic `GeolocationPosition` through
 * `nativeGeolocationHostBridge`, which `useGeoLocation` consumes—same success path as
 * `getCurrentPosition`, without custom events on `window`.
 *
 * Publishes only in a **secure context** (required for the Geolocation API per MDN). Still installs
 * the global on insecure origins so hosts do not hit `ReferenceError` for a missing identifier.
 */

const DEFAULT_ACCURACY_METERS = 65;

type ParsedNativeCoords = {
  lat: number;
  lng: number;
  accuracyMeters: number;
};

function isValidLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

function parseFromPairArgs(
  args: readonly unknown[],
): ParsedNativeCoords | null {
  if (args.length < 2) {
    return null;
  }
  const lat = Number(args[0]);
  const lng = Number(args[1]);
  const accRaw = args.length >= 3 ? Number(args[2]) : Number.NaN;
  const accuracyMeters =
    Number.isFinite(accRaw) && accRaw >= 0 ? accRaw : DEFAULT_ACCURACY_METERS;
  if (!isValidLatLng(lat, lng)) {
    return null;
  }
  return { lat, lng, accuracyMeters };
}

function parseFromObjectArg(first: unknown): ParsedNativeCoords | null {
  if (first === null || typeof first !== 'object' || Array.isArray(first)) {
    return null;
  }
  const o = first as Record<string, unknown>;
  const lat = Number(o.latitude ?? o.lat);
  const lng = Number(o.longitude ?? o.lng);
  const accRaw = Number(o.accuracy ?? o.accuracyMeters);
  const accuracyMeters =
    Number.isFinite(accRaw) && accRaw >= 0 ? accRaw : DEFAULT_ACCURACY_METERS;
  if (!isValidLatLng(lat, lng)) {
    return null;
  }
  return { lat, lng, accuracyMeters };
}

function parseNativeLocationArgs(
  args: readonly unknown[],
): ParsedNativeCoords | null {
  return parseFromPairArgs(args) ?? parseFromObjectArg(args[0]);
}

function buildSyntheticPosition(
  lat: number,
  lng: number,
  accuracyMeters: number,
): GeolocationPosition {
  const timestamp = Date.now();
  const coords: GeolocationCoordinates = {
    accuracy: accuracyMeters,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    latitude: lat,
    longitude: lng,
    speed: null,
    toJSON(): object {
      return {
        accuracy: this.accuracy,
        altitude: this.altitude,
        altitudeAccuracy: this.altitudeAccuracy,
        heading: this.heading,
        latitude: this.latitude,
        longitude: this.longitude,
        speed: this.speed,
      };
    },
  };
  return {
    coords,
    timestamp,
    toJSON(): object {
      return { coords: coords.toJSON(), timestamp };
    },
  };
}

/**
 * Installs `globalThis.window.getLocation` when absent so native `evaluateJavaScript` callbacks do not throw.
 * Hosts may call `globalThis.window.getLocation(lat, lng[, accuracy])` (see MDN `globalThis`).
 * Valid coordinates are forwarded into the app geolocation pipeline via the host bridge.
 */
export function patchGetLocationGlobalIfAbsent(): void {
  const win = globalThis.window;
  if (win === undefined) {
    return;
  }

  const w = win as Window & { getLocation?: unknown };

  if (typeof w.getLocation === 'function') {
    return;
  }

  w.getLocation = function getLocation(...args: unknown[]): void {
    if (isNativeWebViewPageUnloading()) {
      return;
    }

    const parsed = parseNativeLocationArgs(args);
    if (parsed === null) {
      return;
    }

    if (globalThis.isSecureContext !== true) {
      return;
    }

    const position = buildSyntheticPosition(
      parsed.lat,
      parsed.lng,
      parsed.accuracyMeters,
    );

    publishNativeGeolocationPosition(position);
  };
}
